import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { api } from '@/lib/api';
import { fmtDate, fmtDay } from '@/lib/format';
import { CalendarDays, BookOpen, Sparkles, KeyRound, Clock, CalendarClock } from 'lucide-react';
import { StatusBadge } from '@/components/admin/StatusBadge';
import { TID } from '@/lib/testIds';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const Stat = ({ label, value, icon: Icon, accent, to, testId }) => {
  const Card = (
    <div
      data-testid={testId || TID.admin.statCard}
      className="sb-card p-4 relative overflow-hidden hover:border-white/20 transition-colors"
    >
      <div className={`absolute top-0 left-0 h-full w-1 ${accent}`} />
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-white/60">{label}</div>
          <div className="font-display text-3xl text-white mt-1">{value}</div>
        </div>
        <div className="h-10 w-10 rounded-md bg-white/5 grid place-items-center text-[hsl(var(--sb-gold))]">
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
  return to ? <Link to={to}>{Card}</Link> : Card;
};

export default function AdminOverviewPage() {
  const [data, setData] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get('/admin/overview').then(({ data }) => setData(data)).catch(() => {});
  }, []);

  return (
    <AdminLayout>
      <div data-testid={TID.admin.overview}>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
          <div>
            <div className="text-[hsl(var(--sb-green))] uppercase text-xs tracking-widest">Dashboard</div>
            <h1 className="font-display text-3xl sm:text-4xl text-white">TODAY AT SMITHY BROWNS</h1>
          </div>
          <Link
            to="/admin/change-password"
            data-testid="overview-change-password-link"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[hsl(var(--sb-border))] text-white text-sm hover:border-[hsl(var(--sb-gold))]/60 hover:text-[hsl(var(--sb-gold))]"
          >
            <KeyRound size={14} /> Change password
          </Link>
        </div>

        {/* Account meta */}
        <div
          className="sb-card p-4 mb-5 flex flex-wrap items-center gap-x-6 gap-y-2"
          data-testid="overview-account-meta"
        >
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/55">Signed in as</div>
            <div className="text-sm text-white">
              {user?.name} <span className="text-white/55">({user?.email})</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/55">Role</div>
            <div className="text-sm text-white capitalize">{(user?.role || '').replace('_', ' ')}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-1">
              <Clock size={11} /> Last password change
            </div>
            <div className="text-sm text-white" data-testid="overview-last-password-change">
              {user?.password_changed_at
                ? fmtDate(user.password_changed_at)
                : <span className="text-[hsl(var(--sb-red))]">Never — please update</span>}
            </div>
          </div>
        </div>

        {/* Metric cards — reservation & menu operational focus */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat
            label="Reservations today"
            value={data?.today?.reservations_total ?? 0}
            icon={CalendarDays}
            accent="bg-[hsl(var(--sb-green))]"
            to="/admin/reservations"
            testId="stat-reservations-today"
          />
          <Stat
            label="Upcoming reservations"
            value={data?.upcoming_count ?? 0}
            icon={CalendarClock}
            accent="bg-[hsl(var(--sb-gold))]"
            to="/admin/reservations"
            testId="stat-upcoming-reservations"
          />
          <Stat
            label="Menu items"
            value={data?.menu_items_total ?? 0}
            icon={BookOpen}
            accent="bg-blue-500"
            to="/admin/menu"
            testId="stat-menu-items"
          />
          <Stat
            label="Featured dishes"
            value={data?.featured_dishes_total ?? 0}
            icon={Sparkles}
            accent="bg-orange-500"
            to="/admin/featured"
            testId="stat-featured-dishes"
          />
        </div>

        <div className="grid lg:grid-cols-1 gap-4 mt-6">
          <div className="sb-card p-4">
            <div className="flex items-center justify-between">
              <div className="font-display text-xl text-white">UPCOMING RESERVATIONS</div>
              <Link to="/admin/reservations" className="text-xs text-[hsl(var(--sb-gold))]">View all →</Link>
            </div>
            <ul className="mt-3 divide-y divide-[hsl(var(--sb-border))]">
              {(data?.upcoming_reservations || []).slice(0, 10).map((r) => (
                <li key={r.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">
                      {r.reference} · {r.customer?.name}
                    </div>
                    <div className="text-[11px] text-white/55">
                      {fmtDay(r.date)} · {r.time} · {r.party_size} guest{r.party_size === 1 ? '' : 's'}
                    </div>
                  </div>
                  <StatusBadge status={r.status} />
                </li>
              ))}
              {(!data?.upcoming_reservations || !data?.upcoming_reservations.length) && (
                <li className="py-6 text-center text-white/55 text-sm">No upcoming reservations.</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
